import {
  Children,
  createContext,
  isValidElement,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListItem,
  useRole
} from "@floating-ui/react";
import { classNames } from "@ct-react/core";
import { FormCompProps } from "../../hooks/form";
import "./select.scss";

type ContextState = {
  selectedIndex: number | null;
  onSelect: (value: OptionVal) => void;
}

type SelectProps = FormCompProps & {
  children: ReactElement<typeof Option> | Array<ReactElement<typeof Option>>;
}

type OptionVal = { value?: any, label: string };

const SelectContext = createContext<ContextState>({} as ContextState);


const Select = (
  {
    className,
    value: entry,
    placeholder,
    readOnly = false,
    disabled = false,
    onChange,
    onBlur,
    children
  }: SelectProps) => {

  const elementsRef = useRef<HTMLElement[]>([]);
  const labelsRef = useRef<any[]>([]);

  const [ open, setOpen ] = useState(false);
  const [ selectedIndex, setSelectedIndex ] = useState<number | null>(null);

  const value = useMemo(() => entry === "" ? undefined : entry, [ entry ]);
  const allOptions = useMemo(() => Children.toArray(children).filter(isValidElement).map(child => child.props as OptionVal), [ children]);
  const display = useMemo(() => selectedIndex !== null ? allOptions[selectedIndex!] : undefined, [ allOptions, selectedIndex ]);

  useEffect(() => {
    const searchIndex = allOptions.findIndex(o => (o.value || o.label) === value);
    setSelectedIndex(searchIndex < 0 ? null : searchIndex);
  }, [ value ]);

  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: open => {
      setOpen(open);
      !open && onBlur();
    },
    strategy: "absolute",
    placement: "bottom",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      size({
        apply: ({ rects, elements }) => {
          elements.floating.style.minWidth = `${rects.reference.width}px`;
          elements.floating.style.boxSizing = "border-box";
        }
      }),
      flip()
    ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: !readOnly && !disabled }),
    useDismiss(context, { outsidePress: true }),
    useRole(context, { role: "listbox" })
  ]);

  const onSelect = useCallback((value: OptionVal) => {
    onChange(value.value || value.label);
    setOpen(false);
  }, [ onChange ]);

  const wrapperClasses = classNames("custom-select", className, { empty: !value, "read-only": readOnly });

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}
           className={wrapperClasses} tabIndex={0}>
        <span className="value ellipsed">{display?.label || placeholder || ""}</span>
      </div>
      <SelectContext.Provider value={{ selectedIndex, onSelect }}>
        {open &&
          <FloatingFocusManager context={context} modal={false}>
            <div ref={refs.setFloating} {...getFloatingProps()}
                 className="custom-select-items" style={floatingStyles}>
              <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                <ul>{children}</ul>
              </FloatingList>
            </div>
          </FloatingFocusManager>
        }
      </SelectContext.Provider>
    </>);

}

export const Option = ({ value, label }: OptionVal) => {

  const { selectedIndex, onSelect } = useContext(SelectContext);
  const { ref, index } = useListItem({ label });

  const itemClasses = classNames({
    selected: selectedIndex === index
  });

  return (
    <li ref={ref} className={itemClasses}
        onClick={() => onSelect({ value, label })}>
      {label}
    </li>);

}

export default Select;
